import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { subscribeWithSelector } from 'zustand/middleware';
import { useDesktopChatStore } from './desktopChatStore';
import { seedPostOnboardingChatPanelSize } from '../utils/chatPanelSize';

function isDebug(): boolean {
  try {
    return localStorage.getItem('debug_onboarding') === '1';
  } catch {
    return false;
  }
}
const dbg = (...args: unknown[]) => { if (isDebug()) console.log(...args); };

const ONBOARDING_BG_URL = '/landing-pages/riveronboardingbg.webp';

/**
 * Preloads the wizard's full-bleed background image the moment we know the
 * user is heading into the onboarding overlay — well before GeneralOnboardingWizard
 * itself mounts — so the image is already decoded and cached when it renders,
 * eliminating a pop-in/flash on first paint.
 */
function preloadOnboardingBackground(): void {
  try {
    if (document.querySelector(`link[rel="preload"][href="${ONBOARDING_BG_URL}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = ONBOARDING_BG_URL;
    document.head.appendChild(link);
  } catch {
    // DOM unavailable — best-effort
  }
}

/**
 * Onboarding phase machine — a TIMING COORDINATOR, not a feature flag.
 *
 * Its job is to sequence the post-signup reveal so each stage finishes before
 * the next begins (overlay → header → chat → welcome message), preventing UI
 * flicker. The animated cloud overlay it was built around is gone, but the phases
 * are still load-bearing: `Space.tsx` drives the wizard off the `overlay` phase,
 * `SpacePageWrapper` gates header chrome + the welcome message off later phases,
 * and the welcome-message dispatch waits for `chat-complete`.
 *
 * Do NOT collapse these phases into a single boolean — the ordering guarantees
 * are what keep the reveal glitch-free across desktop/mobile. The two onboarding
 * DECISIONS (origin priming + welcome override) live elsewhere
 * (gui/src/config/onboardingOrigin.ts); this store only handles WHEN things
 * happen, never WHETHER a user qualifies.
 *
 * Each phase must complete before the next begins.
 */
export type OnboardingPhase = 
  | 'idle'              // No animation
  | 'overlay'           // Welcome overlay showing
  | 'overlay-complete'  // Overlay done, ready for header
  | 'header'            // Header animating
  | 'header-complete'   // Header done, ready for chat
  | 'chat'              // Chat animating (desktop) or initializing (mobile)
  | 'chat-complete'     // Chat ready, can send welcome message
  | 'welcome-sent'      // Welcome message sent, sequence complete
  | 'complete';         // All done, cleanup

/**
 * In-chat default onboarding ('chat' mode, see generalFlowConfig.ts) stage.
 * Runtime-only — a reload simply drops back to normal chrome; the persona
 * buttons / chips already injected into chat history remain clickable and
 * still work regardless of this flag (see SpaceChat's action handlers).
 *
 *  - 'awaiting_persona'      → welcome message + persona buttons shown, chrome hidden
 *  - 'awaiting_first_message' → persona picked, chips shown, chrome still hidden
 *  - null                    → onboarding not active / already ended, normal chrome
 */
export type ChatOnboardingStage = 'awaiting_persona' | 'awaiting_first_message' | null;

interface OnboardingState {
  // Session flags (runtime-only, not persisted — source of truth is the backend user profile)
  isNewUser: boolean;
  shouldOpenChat: boolean;
  welcomeModalShown: boolean;
  successModalShown: boolean;
  voiceConfigPromptShown: boolean;

  // Animation control flags (persisted)
  useAnimatedIntro: boolean;
  cameFromAnimatedIntro: boolean;

  // Animation state (not persisted - runtime only)
  currentPhase: OnboardingPhase;
  overlayComplete: boolean;
  headerAnimationComplete: boolean;
  chatAnimationComplete: boolean;
  welcomeMessageSent: boolean;
  
  // Captured data for welcome message
  capturedThreadId: string | null;
  chatStoreInitialized: boolean;

  /**
   * Set the instant SpacePageWrapper decides to dispatch the 'chat-complete'
   * welcome/kickoff message into `capturedThreadId`, and cleared only once
   * that dispatch has actually settled (message added, or the attempt failed/
   * was skipped). NOT the same thing as `welcomeMessageSent`/`currentPhase`
   * reaching 'complete' — those flip immediately on dispatch, well before an
   * async welcome flow's own awaited work (thread reconciliation, an LLM
   * call, a typewriter animation) actually lands the message. See
   * `isAnimatedOnboardingWelcomePending`.
   */
  welcomeDispatchThreadId: string | null;
  setWelcomeDispatchThreadId: (threadId: string | null) => void;

  // Cosmos first-run onboarding (runtime-only, not persisted)
  cosmosMode: boolean;
  firstRunComplete: boolean;
  /**
   * Message to dispatch as the first user message when the next space loads.
   * Set by any agentic flow (cosmos onboarding, space wizards, etc.) before navigating.
   * SpacePageWrapper picks it up, dispatches it once the chatstore is ready, then clears it.
   */
  pendingSpaceKickoff: string | null;
  /**
   * Optional chip metadata for the kickoff message. When set, the user message bubble
   * is replaced by a compact chip (icon + label) instead of showing the raw prompt text.
   * Only set by admin-invite redemption paths; all other kickoff callers leave this null.
   */
  /**
   * Configures the initial window that should be opened when a new user first
   * lands in their space. Set before navigation by signup/funnel flows.
   * SpacePageWrapper opens it as soon as the space + chat are ready, independent
   * of the phase machine (which can get stuck in desktop layout mode).
   *
   * { type: 'getting_started' } — resolve the Getting Started doc by name/skeleton
   * { type: 'entity_id', entityId: string } — open a specific entity directly
   */
  pendingInitialWindow: { type: 'getting_started' } | { type: 'entity_id'; entityId: string } | null;
  setPendingInitialWindow: (config: OnboardingState['pendingInitialWindow']) => void;
  pendingSpaceKickoffChip: { label: string; icon?: string } | null;
  /**
   * Persona id selected by the wizard's single onboarding question, stored after
   * the user completes it. Used by sendWelcomeMessageToChat (wizard path) to
   * build a persona-specific short greeting + quick-prompt chips. Cleared after use.
   */
  pendingOnboardingPersona: string | null;

  /** See ChatOnboardingStage. */
  chatOnboardingStage: ChatOnboardingStage;
  setChatOnboardingStage: (stage: ChatOnboardingStage) => void;
  /**
   * Wall-clock ms timestamp captured when the persona step begins (welcome
   * message shown). Not persisted — only exists to compute `durationMs` for
   * the `general_onboarding_step_completed` analytics event on persona pick.
   */
  chatOnboardingStepStartedAt: number | null;

  setCosmosMode: (value: boolean) => void;
  setFirstRunComplete: (value: boolean) => void;
  setPendingSpaceKickoff: (message: string | null, chip?: { label: string; icon?: string } | null) => void;
  setPendingOnboardingPersona: (persona: string | null) => void;

  // Actions
  setIsNewUser: (value: boolean) => void;
  setShouldOpenChat: (value: boolean) => void;
  setWelcomeModalShown: (value: boolean) => void;
  setSuccessModalShown: (value: boolean) => void;
  setVoiceConfigPromptShown: (value: boolean) => void;
  setUseAnimatedIntro: (value: boolean) => void;
  setCameFromAnimatedIntro: (value: boolean) => void;
  setOverlayComplete: (value: boolean) => void;
  setHeaderAnimationComplete: (value: boolean) => void;
  setChatAnimationComplete: (value: boolean) => void;
  setWelcomeMessageSent: (value: boolean) => void;
  
  // State machine actions
  setCapturedThreadId: (threadId: string) => void;
  setChatStoreInitialized: (value: boolean) => void;
  advancePhase: () => void;
  resetAnimationState: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Initial state
        isNewUser: false,
        shouldOpenChat: false,
        welcomeModalShown: false,
        successModalShown: false,
        voiceConfigPromptShown: false,
        useAnimatedIntro: true, // Enable animated intro by default
        cameFromAnimatedIntro: false,
        
        // Runtime state (not persisted)
        currentPhase: 'idle',
        overlayComplete: false,
        headerAnimationComplete: false,
        chatAnimationComplete: false,
        welcomeMessageSent: false,
        capturedThreadId: null,
        chatStoreInitialized: false,
        welcomeDispatchThreadId: null,
        setWelcomeDispatchThreadId: (threadId) => {
          dbg('[OnboardingStore] ✏️ setWelcomeDispatchThreadId:', threadId);
          set({ welcomeDispatchThreadId: threadId });
        },

        // Cosmos first-run onboarding (runtime-only)
        cosmosMode: false,
        firstRunComplete: false,
        pendingSpaceKickoff: null,
        pendingInitialWindow: null,

        setPendingInitialWindow: (config) => {
          dbg('[OnboardingStore] ✏️ setPendingInitialWindow:', config);
          set({ pendingInitialWindow: config });
        },
        pendingSpaceKickoffChip: null,
        pendingOnboardingPersona: null,

        chatOnboardingStage: null,
        chatOnboardingStepStartedAt: null,
        setChatOnboardingStage: (stage) => {
          dbg('[OnboardingStore] ✏️ setChatOnboardingStage:', stage);
          set({
            chatOnboardingStage: stage,
            chatOnboardingStepStartedAt: stage === null ? null : Date.now(),
          });
        },

        setCosmosMode: (value) => {
          dbg('[OnboardingStore] ✏️ setCosmosMode:', value);
          set({ cosmosMode: value });
        },

        setFirstRunComplete: (value) => {
          dbg('[OnboardingStore] ✏️ setFirstRunComplete:', value);
          set({ firstRunComplete: value });
        },

        setPendingSpaceKickoff: (message, chip) => {
          dbg('[OnboardingStore] ✏️ setPendingSpaceKickoff:', message?.slice(0, 80) ?? null, chip ?? null);
          set({ pendingSpaceKickoff: message, pendingSpaceKickoffChip: message ? (chip ?? null) : null });
        },

        setPendingOnboardingPersona: (persona) => {
          dbg('[OnboardingStore] ✏️ setPendingOnboardingPersona:', persona);
          set({ pendingOnboardingPersona: persona });
        },

        setIsNewUser: (value) => {
          dbg('[OnboardingStore] ✏️ setIsNewUser:', value);
          set({ isNewUser: value });
        },

        setShouldOpenChat: (value) => {
          dbg('[OnboardingStore] ✏️ setShouldOpenChat:', value);
          set({ shouldOpenChat: value });
        },

        setWelcomeModalShown: (value) => {
          dbg('[OnboardingStore] ✏️ setWelcomeModalShown:', value);
          set({ welcomeModalShown: value });
        },

        setSuccessModalShown: (value) => {
          dbg('[OnboardingStore] ✏️ setSuccessModalShown:', value);
          set({ successModalShown: value });
        },
        
        setVoiceConfigPromptShown: (value) => {
          dbg('[OnboardingStore] ✏️ setVoiceConfigPromptShown:', value);
          set({ voiceConfigPromptShown: value });
        },

        setUseAnimatedIntro: (value) => {
          dbg('[OnboardingStore] ✏️ setUseAnimatedIntro:', value);
          set({ useAnimatedIntro: value });
        },

        setCameFromAnimatedIntro: (value) => {
          dbg('[OnboardingStore] ✏️ setCameFromAnimatedIntro:', value);

          // When starting new animation sequence, reset all completion flags
          if (value === true) {
            dbg('[OnboardingStore] 🔄 Starting new onboarding sequence - resetting to overlay phase');
            preloadOnboardingBackground();
            set({
              cameFromAnimatedIntro: true,
              useAnimatedIntro: true,
              currentPhase: 'overlay',
              overlayComplete: false,
              headerAnimationComplete: false,
              chatAnimationComplete: false,
              welcomeMessageSent: false,
              capturedThreadId: null,
              chatStoreInitialized: false,
            });
          } else {
            set({ cameFromAnimatedIntro: value });
          }
        },

        setOverlayComplete: (value) => {
          dbg('[OnboardingStore] ✏️ setOverlayComplete:', value);
          set({ overlayComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setHeaderAnimationComplete: (value) => {
          dbg('[OnboardingStore] ✏️ setHeaderAnimationComplete:', value);
          set({ headerAnimationComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setChatAnimationComplete: (value) => {
          dbg('[OnboardingStore] ✏️ setChatAnimationComplete:', value);
          set({ chatAnimationComplete: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        setWelcomeMessageSent: (value) => {
          dbg('[OnboardingStore] ✏️ setWelcomeMessageSent:', value);
          set({ welcomeMessageSent: value });
          
          if (value) {
            get().advancePhase();
          }
        },
        
        setCapturedThreadId: (threadId) => {
          dbg('[OnboardingStore] 📥 setCapturedThreadId:', threadId);
          set({ capturedThreadId: threadId });
        },
        
        setChatStoreInitialized: (value) => {
          dbg('[OnboardingStore] ✏️ setChatStoreInitialized:', value);
          set({ chatStoreInitialized: value });
          
          if (value) {
            get().advancePhase();
          }
        },

        /**
         * State machine: Advance to next phase based on current state
         * This eliminates race conditions by having clear phase transitions
         * Advances multiple times if needed (for transition phases)
         */
        advancePhase: () => {
          let state = get();
          let { currentPhase, overlayComplete, headerAnimationComplete, chatStoreInitialized, chatAnimationComplete, welcomeMessageSent } = state;
          
          dbg('[OnboardingStore] 🎯 advancePhase called:', { currentPhase, overlayComplete, headerAnimationComplete, chatStoreInitialized, chatAnimationComplete, welcomeMessageSent });
          
          let advanced = false;
          let iterations = 0;
          const MAX_ITERATIONS = 10; // Prevent infinite loops
          
          // Keep advancing until we reach a stable phase
          while (iterations < MAX_ITERATIONS) {
            iterations++;
            let nextPhase: OnboardingPhase = currentPhase;
            
            switch (currentPhase) {
              case 'idle':
                // No animation active
                break;
                
              case 'overlay':
                if (overlayComplete) {
                  nextPhase = 'overlay-complete';
                  dbg('[OnboardingStore] 🎬 Overlay complete → Header can start');
                }
                break;
                
              case 'overlay-complete':
                // Transition phase - immediately advance to header
                nextPhase = 'header';
                dbg('[OnboardingStore] ⏭️ Auto-advancing overlay-complete → header');
                break;
                
              case 'header':
                if (headerAnimationComplete) {
                  nextPhase = 'header-complete';
                  dbg('[OnboardingStore] 🎬 Header complete → Chat can start');
                }
                break;
                
              case 'header-complete':
                if (chatStoreInitialized) {
                  nextPhase = 'chat';
                  dbg('[OnboardingStore] ⏭️ ChatStore ready → Advancing to chat phase');
                } else {
                  dbg('[OnboardingStore] ⏸️ Waiting for chatStore initialization before starting animations');
                }
                break;
                
              case 'chat':
                if (chatAnimationComplete) {
                  nextPhase = 'chat-complete';
                  dbg('[OnboardingStore] 🎬 Chat animation complete → Welcome message can send');
                }
                break;
                
              case 'chat-complete':
                if (welcomeMessageSent) {
                  nextPhase = 'welcome-sent';
                  dbg('[OnboardingStore] 🎬 Welcome sent → Sequence complete');
                }
                break;
                
              case 'welcome-sent':
                nextPhase = 'complete';
                // Clear cameFromAnimatedIntro flag after everything is done
                set({ cameFromAnimatedIntro: false });
                dbg('[OnboardingStore] ✅ Animation sequence COMPLETE');
                break;
                
              case 'complete':
                // Fully done
                break;
            }
            
            if (nextPhase !== currentPhase) {
              dbg('[OnboardingStore] ⏭️ Phase transition:', currentPhase, '→', nextPhase);
              currentPhase = nextPhase;
              advanced = true;
              
              // Update state
              set({ currentPhase: nextPhase });
              
              // Emit event for components that need to react to phase changes
              window.dispatchEvent(new CustomEvent('onboarding-phase-change', {
                detail: { from: state.currentPhase, to: nextPhase }
              }));
              
              // Update local state for next iteration
              state = get();
              chatStoreInitialized = state.chatStoreInitialized;
              chatAnimationComplete = state.chatAnimationComplete;
              welcomeMessageSent = state.welcomeMessageSent;
            } else {
              // No more transitions possible
              break;
            }
          }
          
          if (iterations >= MAX_ITERATIONS) {
            console.error('[OnboardingStore] ⚠️ Maximum iterations reached in advancePhase - possible infinite loop');
          }
          
          if (advanced) {
            dbg('[OnboardingStore] ✅ Final phase after advancement:', get().currentPhase);
          }
        },

        resetAnimationState: () => {
          dbg('[OnboardingStore] 🔄 Resetting animation state');
          set({
            currentPhase: 'idle',
            overlayComplete: false,
            headerAnimationComplete: false,
            chatAnimationComplete: false,
            welcomeMessageSent: false,
            capturedThreadId: null,
            chatStoreInitialized: false,
            cameFromAnimatedIntro: false,
            welcomeDispatchThreadId: null,
          });
        },
      }),
      {
        name: 'onboarding-storage',
        storage: createJSONStorage(() => localStorage),
        // Only persist user profile flags, not runtime animation state
        partialize: (state) => ({
  // Only persist the animated intro UI preference
          // All "shown once" tracking flags are backend-backed via userStore.setOnboardingFlag
          useAnimatedIntro: state.useAnimatedIntro,
        }),
      },
    ),
  ),
);

/**
 * True while SpacePageWrapper's 'chat-complete'-triggered welcome/kickoff
 * dispatch is actively in flight for `threadId` (see `welcomeDispatchThreadId`).
 *
 * SpaceChat's generic `empty-chat-welcome` safety-net effect (which fires a
 * default greeting into ANY confirmed-empty thread) uses this to avoid racing
 * that dispatch. Deliberately NOT keyed off `currentPhase`/`welcomeMessageSent`
 * — those flip to their "done" values the instant the dispatch is *started*
 * (fire-and-forget), not once it actually lands a message, so they cannot
 * detect the real risk window: the welcome flow's own awaited work (thread
 * reconciliation, an LLM call, a typewriter animation) can still be in
 * progress well after `currentPhase` already reads 'complete'. During that
 * window, neither local conversationStore state nor a backend message-count
 * check can reliably prove "a welcome message is about to land here", so the
 * generic effect would otherwise win the race and inject its own greeting
 * first, leaving both stacked in the thread.
 */
export function isAnimatedOnboardingWelcomePending(threadId: string | null): boolean {
  if (!threadId) return false;
  return useOnboardingStore.getState().welcomeDispatchThreadId === threadId;
}

/**
 * Ends the in-chat onboarding flow: clears the transient stage and dedocks
 * the intake-centered chat back to normal docked presentation. Idempotent —
 * safe to call multiple times, or after a reload has already cleared state
 * (chatOnboardingStage is already null), so every call site (chip click,
 * free-typed first message, injection-failure fallback) can call it
 * unconditionally.
 */
export function endChatOnboarding(): void {
  if (useOnboardingStore.getState().chatOnboardingStage === null) return;
  useOnboardingStore.getState().setChatOnboardingStage(null);
  useDesktopChatStore.getState().endIntake();
  // A little bigger than the plain default once it lands in its docked
  // corner — no-ops if the panel already has a stored width (including the
  // first-signup seed, which may already be even wider).
  seedPostOnboardingChatPanelSize();
}